import { Users, Gift, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';

interface OnboardingSlideProps {
  onComplete: () => void;
  isCompleting: boolean;
  isLastSlide: boolean;
  onNext: () => void;
  onGenderSelect?: (gender: string) => void;
}

export const OnboardingSlideBeta = ({ onNext }: OnboardingSlideProps) => {
  const [isJoining, setIsJoining] = useState(false);

  const handleJoinBeta = async () => {
    setIsJoining(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // User not authenticated yet, just continue to next slide
        onNext();
        return;
      }

      // Record consent
      await supabase
        .from('profiles')
        .update({ beta_consent_at: new Date().toISOString() })
        .eq('id', user.id);

      // If not already added via invite, add to participants
      const { error } = await supabase.rpc('add_beta_participant', {
        p_user_id: user.id,
        p_cohort_name: 'Onboarding Opt-In'
      });

      if (error && error.code !== '23505') throw error; // Ignore duplicate errors

      onNext();
    } catch (error: any) {
      console.error('Error joining beta:', error);
      onNext(); // Continue anyway
    } finally {
      setIsJoining(false);
    }
  };


  return (
    <Card className="shadow-card border-0 bg-gradient-card">
      <CardContent className="flex flex-col items-center justify-center min-h-[500px] px-6 py-8">
      <div className="max-w-md w-full space-y-6">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="h-24 w-24 rounded-2xl bg-primary text-white flex items-center justify-center">
            <span className="text-5xl font-bold">B</span>
          </div>
        </div>

        {/* Title */}
        <div className="text-center space-y-2">
          <h2 className="text-xl md:text-2xl font-semibold">
            Join the Beta Program
          </h2>
          <p className="text-sm text-muted-foreground">
            Be part of shaping FaceFable™ and earn exclusive rewards
          </p>
        </div>

        {/* Benefits */}
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Early Access</h3>
              <p className="text-xs text-muted-foreground">
                Try new features before anyone else
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Gift className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Earn Rewards</h3>
              <p className="text-xs text-muted-foreground">
                Get points for feedback and contributions
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Shape the Future</h3>
              <p className="text-xs text-muted-foreground">
                Your voice directly influences development
              </p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3 pt-4">
          <Button
            onClick={handleJoinBeta}
            disabled={isJoining}
            className="w-full"
            size="lg"
          >
            {isJoining ? 'Joining...' : 'Join Beta Program'}
          </Button>
        </div>
      </div>
      </CardContent>
    </Card>
  );
};
